import RippleCard from '@/ui/card/RippleCard';
import {
  BitCoin,
  Gifts,
  Plus,
  Points,
  RightArrow,
  RightArrowTwo,
  SecurityTwo,
} from '@/ui/svg';
import { formatNumber } from '@/utils';
import { useProfile, useStake, useToken, useWallets } from '@bitbot/model';
import clsx from 'clsx';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { useMemo, useState } from 'react';
import SwipeableViews from 'react-swipeable-views';
import { autoPlay } from 'react-swipeable-views-utils';
import CommonSkeleton from '../common/skeleton/common-skeleton';
import ReadyToClaimDialog from './ready-to-claim-dialog';
import HapticContainer from '../common/haptic-container';

const AutoPlaySwipeableViews = autoPlay(SwipeableViews);

export default function Card() {
  const { stakingStatus } = useStake();
  const { tradeLoginStatus } = useToken();
  const { userInfo, gettingUserInfo, isEmailEnabled } = useProfile();
  const router = useRouter();
  const [showReadyClaim, setShowReadyClaim] = useState(false);
  const { initingWalletInfo } = useWallets();

  const [index, setIndex] = useState(0);
  const showLinkEmail =
    tradeLoginStatus && !isEmailEnabled && !initingWalletInfo;

  const stake = useMemo(
    () => (
      <div className="p-4 rounded-[1.125rem] bg-[#14044F] mt-4 mx-4 h-[180px]">
        <div className="flex justify-between">
          <div className="flex flex-col">
            <div className="flex items-center gap-1 text-sm">
              <BitCoin className="text-[#9896EF]" />
              <span className="linear-text font-bold">Staked $BITBOT</span>
            </div>
            <div className="font-rubik text-[1.875rem] font-medium mt-1 h-[2.8125rem]">
              {gettingUserInfo ? (
                <CommonSkeleton
                  className="w-[120px] h-9"
                  baseColor="#FFFFFF14"
                  highlightColor="#FFFFFF20"
                />
              ) : userInfo ? (
                formatNumber(
                  typeof userInfo?.staked_mount === 'undefined'
                    ? +userInfo?.staked_mount
                    : 0,
                )
              ) : (
                ' '
              )}
            </div>
          </div>
          <RippleCard
            onClick={() => {
              router.push(
                '/trade?pairAddress=0xf87e9c413f5d50471aa46d13Fa69e5a09039205e',
              );
            }}
            className="w-7 h-7 rounded-full bg-white text-black flex justify-center items-center cursor-pointer"
          >
            <Plus />
          </RippleCard>
        </div>
        <RippleCard
          className="flex w-full justify-between mt-4 items-center cursor-pointer"
          onClick={() => {
            router.push('/stake');
          }}
        >
          <div className="w-16 h-16 flex items-center justify-center rounded-[12px] bg-black/25 backdrop-blur-[20px]">
            <Image
              src="/images/coin.svg"
              width={51}
              height={51}
              alt=""
              className="mx-auto transition-all"
            />
          </div>
          <div className="flex flex-col flex-grow pl-4 pr-2">
            <div className="text-lg h-7 flex items-center">
              {gettingUserInfo ? (
                <CommonSkeleton
                  className="h-5 w-40"
                  baseColor="#FFFFFF14"
                  highlightColor="#FFFFFF20"
                />
              ) : stakingStatus ? (
                stakingStatus.availableAmount === 0 ? (
                  `${formatNumber(
                    stakingStatus?.earnedAmount ?? 0,
                  )} earned`
                ) : (
                  'View my staking'
                )
              ) : (
                ' '
              )}
            </div>
            <div className="text-sm text-secondary tracking-[-0.07px] h-5 flex items-center">
              {gettingUserInfo ? (
                <CommonSkeleton
                  className="h-4 w-40"
                  baseColor="#FFFFFF14"
                  highlightColor="#FFFFFF20"
                />
              ) : stakingStatus ? (
                'View my staking'
              ) : (
                ' '
              )}
            </div>
          </div>
          <RightArrow />
        </RippleCard>
      </div>
    ),
    [gettingUserInfo, stakingStatus, userInfo, router],
  );

  const points = useMemo(
    () => (
      <div className="p-4 rounded-[1.125rem] bg-[#14044F] mt-4 mx-4 h-[180px]">
        <div className="flex justify-between">
          <div className="flex flex-col">
            <div className="flex items-center gap-1 text-sm">
              <Points className="w-4 h-4 text-[#9896EF]" />
              <span className="linear-text font-bold">
                Bitpoints Earned
              </span>
            </div>
            <div className="font-rubik text-[1.875rem] font-medium mt-1 h-[2.8125rem]">
              {gettingUserInfo ? (
                <CommonSkeleton
                  className="w-[120px] h-9"
                  baseColor="#FFFFFF14"
                  highlightColor="#FFFFFF20"
                />
              ) : userInfo ? (
                formatNumber(
                  typeof userInfo?.point_reward === 'undefined'
                    ? +userInfo?.point_reward
                    : 0,
                )
              ) : (
                ' '
              )}
            </div>
          </div>
          <RippleCard
            onClick={() => {
              router.push('/profile/referral');
            }}
            className="w-7 h-7 rounded-full bg-white text-black flex justify-center items-center cursor-pointer"
          >
            <Gifts />
          </RippleCard>
        </div>
        <RippleCard
          className="flex w-full justify-between mt-4 items-center cursor-pointer"
          onClick={() => {
            router.push('/profile/points');
          }}
        >
          <div className="w-16 h-16 flex items-center justify-center rounded-[12px]  backdrop-blur-[20px]">
            <Image
              src="/images/points.png"
              width={256}
              height={256}
              alt=""
              className="mx-auto transition-all w-16 h-16"
            />
          </div>
          <div className="flex flex-col flex-grow pl-4 pr-2">
            <div className="text-lg">Claim BitPoints</div>
            <div className="text-lg">and exchange to USDT</div>
          </div>
          <RightArrow />
        </RippleCard>
      </div>
    ),
    [userInfo, gettingUserInfo, router],
  );

  const cardLength = useMemo(() => {
    if (showLinkEmail) return 3;
    return 2;
  }, [showLinkEmail]);

  const linkEmail = useMemo(
    () => (
      <div className="p-4 rounded-[1.125rem] bg-[#14044F] mt-4 mx-4 h-[180px] overflow-hidden">
        <div className="flex justify-between">
          <div className="flex flex-col">
            <div className="flex items-center gap-1 text-sm">
              <SecurityTwo className="text-[#2997FF]" />
              <span className="linear-text2 font-bold">
                $BITBOT SECURITY
              </span>
            </div>
            <div className="mt-4">
              <div className="text-xl font-bold">Link Email</div>
              <div className="text-tertiary tracking-[-0.2px] text-[3.72vw]">
                Protect your funds’ security
              </div>
            </div>
            <div className="flex mt-4">
              <div
                onClick={() => {
                  router.push('/profile/add-email');
                }}
                className="bg-reverse cursor-pointer text-sm font-bold flex-grow-0 text-reverse rounded-[14px] flex items-center justify-center h-9 px-6"
              >
                Go Link
                <RightArrowTwo />
              </div>
            </div>
          </div>
          <Image
            src="/images/2fa/shield.png"
            width={158}
            height={206}
            alt=""
            className="mr-4 w-[27vw]"
          />
        </div>
      </div>
    ),
    [router],
  );
  const invite = useMemo(() => {
    return (
      <div className="p-4 rounded-[1.125rem] bg-inviteCard mt-4 mx-4 h-[180px] overflow-hidden flex items-center">
        <HapticContainer
          className=" flex-1"
          onClick={() => {
            router.push('/invite');
          }}
        >
          <div className="flex items-center gap-[6px]">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_132_102679)">
                <path
                  d="M8 0.666504L13.478 1.88384C13.626 1.91675 13.7584 1.99915 13.8533 2.11744C13.9482 2.23573 14 2.38285 14 2.5345V9.1925C14 9.85099 13.8373 10.4993 13.5266 11.0798C13.2158 11.6604 12.7666 12.1553 12.2187 12.5205L8 15.3332L3.78133 12.5205C3.23352 12.1553 2.78431 11.6606 2.47357 11.0801C2.16282 10.4997 2.00016 9.85155 2 9.19317V2.5345C2.00003 2.38285 2.05176 2.23573 2.14666 2.11744C2.24156 1.99915 2.37396 1.91675 2.522 1.88384L8 0.666504ZM8 2.0325L3.33333 3.06917V9.1925C3.33334 9.63148 3.44172 10.0637 3.64884 10.4507C3.85597 10.8377 4.15543 11.1677 4.52067 11.4112L8 13.7312L11.4793 11.4112C11.8445 11.1677 12.1439 10.8379 12.351 10.451C12.5581 10.0641 12.6665 9.63203 12.6667 9.19317V3.06917L8 2.03317V2.0325ZM8 4.6665C8.29356 4.66637 8.57896 4.76312 8.81191 4.94174C9.04487 5.12037 9.21237 5.37088 9.28842 5.65442C9.36447 5.93796 9.34482 6.23866 9.23252 6.50989C9.12023 6.78112 8.92156 7.00771 8.66733 7.1545L8.66667 9.99984H7.33333V7.1545C7.07916 7.00774 6.88052 6.78121 6.76821 6.51005C6.65589 6.23889 6.63619 5.93824 6.71216 5.65474C6.78812 5.37124 6.95551 5.12073 7.18835 4.94205C7.4212 4.76337 7.7065 4.66652 8 4.6665Z"
                  fill="#2997FF"
                />
              </g>
              <defs>
                <clipPath id="clip0_132_102679">
                  <rect width="16" height="16" fill="white" />
                </clipPath>
              </defs>
            </svg>

            <h4 className="uppercase invite-text font-nunito text-sm font-bold">
              Lucky DRAW
            </h4>
          </div>
          <p className="mt-3 mb-4 font-nunito_sans text-xl">
            You have 100 USDC reward!
          </p>
          <div className="flex items-center w-[123px] h-[36px] bg-white rounded-[14px] text-sm justify-center text-[#0B0C0E] font-bold">
            Go Spin
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13.4766 9.1664L9.00665 4.6964L10.185 3.51807L16.6666 9.99973L10.185 16.4814L9.00665 15.3031L13.4766 10.8331H3.33331V9.1664H13.4766Z"
                fill="#0B0C0E"
              />
            </svg>
          </div>
        </HapticContainer>
        <Image
          src="/invite/gift-open.png?v=2"
          alt="usdt"
          width={280}
          height={280}
          className="size-[140px]"
        />
      </div>
    );
  }, []);

  const adex = useMemo(() => {
    return (
      <div className="rounded-[1.125rem] bg-inviteCard mt-4 mx-4 h-[180px] overflow-hidden flex items-center">
        <Image
          src="/home/hala.png"
          alt="usdt"
          height={360}
          width={(360 / 516) * 1024}
          className="w-full h-[180px]"
        />
      </div>
    );
  }, []);
  return (
    <>
      {showLinkEmail ? (
        <AutoPlaySwipeableViews
          index={index}
          onChangeIndex={setIndex}
          enableMouseEvents
        >
          {linkEmail}
          {invite}
          {/* {stake} */}
          {points}
        </AutoPlaySwipeableViews>
      ) : (
        <AutoPlaySwipeableViews
          index={index}
          onChangeIndex={setIndex}
          enableMouseEvents
        >
          {/* {stake} */}
          {adex}
          {/* {points} */}
        </AutoPlaySwipeableViews>
      )}
      {/* <div className="flex gap-1 justify-center my-3 h-[0.375rem]">
        {cardLength > 1
          ? Array.from({ length: cardLength }, (_, index) => index).map(
              item => (
                <div
                  key={item}
                  onClick={() => setIndex(item)}
                  className={clsx(
                    'w-[0.375rem] h-[0.375rem] rounded-full cursor-pointer',
                    index === item ? 'bg-white' : 'bg-secondary',
                  )}
                ></div>
              ),
            )
          : ''}
      </div> */}
      <ReadyToClaimDialog
        open={showReadyClaim}
        toggle={setShowReadyClaim}
      />
    </>
  );
}
